import {useConfig} from '@aboutyou/config';
import {useLegacyGrpcRequestCallback} from '@aboutyou/data-fetcher';
import {PaymentsService_setRefundBankAccountLazy} from '@aboutyou/grpc-clients/grips/protos/payments/v1/payments_service.grpc.lazy';
import {
  Device,
  RequestConfig,
} from '@aboutyou/grpc-clients/grips/protos/shared/shared.types';
import {MissingBankDataSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/order_success_page/v1/sections.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  Button,
  COLORS,
  FONT_SIZES,
  GenericIconName,
  Icon,
  Text,
  TextInput,
  tabletAndUpLinaria,
  useShowToast,
} from '@aboutyou/ui-components';
import {MaybeTokenOrSessionId, useUserTokenOrSessionId} from '@aboutyou/user';
import {styled} from '@linaria/react';
import iban from 'iban';
import React from 'react';
import {Controller, useForm} from 'react-hook-form';

interface BankDataFormInputs {
  firstName: string;
  lastName: string;
  iban: string;
}

const ACTIVE_STYLES = {
  backgroundColor: COLORS.Grey4,
  backgroundColorFocused: COLORS.White,
  borderColor: COLORS.Black,
};

/**
 * We need this function in order to construct a legacy session object
 * which can be consumed by grips
 * @param tokenOrSessionId the tadarida token or session id
 * @returns a legacy session object
 */
const getSession = (
  tokenOrSessionId: MaybeTokenOrSessionId,
): RequestConfig['session'] => {
  if (!tokenOrSessionId) {
    return;
  }

  return tokenOrSessionId && 'token' in tokenOrSessionId
    ? {
        $case: 'customerSession' as const,
        customerSession: {authToken: tokenOrSessionId.token},
      }
    : {
        $case: 'guestSession' as const,
        guestSession: {sessionId: (tokenOrSessionId as any)?.sessionId},
      };
};

const useUpdateBankData = () => {
  const showToast = useShowToast();
  const {t} = useTranslation();

  const mutation = useLegacyGrpcRequestCallback(
    PaymentsService_setRefundBankAccountLazy,
    {
      onSuccess() {
        showToast({
          content: t('Bank data successfully submitted.'),
          type: 'SUCCESS',
        });
      },
    },
  );
  const {data: tokenOrSessionId} = useUserTokenOrSessionId();

  const {device, legacyConfig} = useConfig();

  const updateBankData = (data: BankDataFormInputs) => {
    mutation
      .mutateAsync({
        config: {
          device: device === 'desktop' ? Device.DESKTOP : Device.MOBILE_WEB,
          childShopId: legacyConfig.childShopId,
          shopId: legacyConfig.shopId,
          session: getSession(tokenOrSessionId),
        },
        bankAccount: {
          firstName: data.firstName,
          lastName: data.lastName,
          iban: data.iban,
          // bic is also set to empty string in legacy
          bic: '',
        },
      })
      .catch(() => {
        // no specific catch handling needed, apparently the use grpc request throws
      });
  };

  return {
    isLoading: mutation.isPending,
    mutate: updateBankData,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
  };
};

export const BankDataForm = (props: MissingBankDataSection) => {
  const {headline, subtitle, ctaLabel} = props;

  const {t} = useTranslation();

  const {
    register,
    handleSubmit,
    formState: {errors},
    control,
  } = useForm<BankDataFormInputs>();

  const {isLoading, mutate, isError, isSuccess} = useUpdateBankData();

  const onSubmit = (data: BankDataFormInputs) => {
    mutate(data);
  };

  const ibanRequiredMessage = t('IBAN is required');
  const ibanInvalidMessage = t('IBAN is invalid');

  if (isSuccess) {
    return null;
  }

  return (
    <BankDataStreamWrapper>
      <FormContainer data-testid="bankDataForm">
        <TextContainer>
          <Icon
            asset={GenericIconName.Error}
            size="M"
            fill={COLORS.Orange600}
          />

          <Headline textAlignment="center" weight="mediumHeavy">
            {headline}
          </Headline>
          <Subtitle textAlignment="center" weight="mediumBold">
            {subtitle}
          </Subtitle>
          {isError && (
            <Text
              textAlignment="center"
              size="px14"
              weight="mediumBold"
              color="Red"
            >
              {t('Please fill in a valid value for all required fields.')}
            </Text>
          )}
        </TextContainer>

        <Form onSubmit={handleSubmit(onSubmit)}>
          <TopContainer>
            <TextInput
              activeStyle={ACTIVE_STYLES}
              label={t('First name of Account Holder')}
              id="firstName"
              {...register('firstName', {
                required: t('First name is required'),
              })}
              errorMessage={errors.firstName?.message}
              data-testid="firstNameInput"
            />

            <TextInput
              activeStyle={ACTIVE_STYLES}
              label={t('Last name of Account Holder')}
              id="lastName"
              {...register('lastName', {required: t('Last name is required')})}
              errorMessage={errors.lastName?.message}
              data-testid="lastNameInput"
            />
          </TopContainer>

          <Controller
            name="iban"
            control={control}
            render={({field}) => (
              <TextInput
                activeStyle={ACTIVE_STYLES}
                id="iban"
                label={t('IBAN')}
                errorMessage={errors.iban?.message}
                value={field.value}
                onChange={field.onChange}
                ref={field.ref}
                data-testid="ibanInput"
              />
            )}
            rules={{
              validate: {
                required: value => {
                  if (!value) return ibanRequiredMessage;
                  if (!iban.isValid(value)) return ibanInvalidMessage;
                },
              },
            }}
            defaultValue=""
          />

          <Button
            isLoading={isLoading}
            htmlType="submit"
            data-testid="submitBankDetails"
          >
            {ctaLabel}
          </Button>
        </Form>
      </FormContainer>
    </BankDataStreamWrapper>
  );
};

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`;

const TopContainer = styled.div`
  display: flex;
  gap: 1rem;
  flex-direction: column;
  ${tabletAndUpLinaria(`
    flex-direction: row;
  `)}
`;

export const BankDataStreamWrapper = styled.div`
  padding: 1.5rem 1rem;
  ${tabletAndUpLinaria(`
    margin: 0 auto;
    padding: 1.5rem 0;
  `)}
  background-color: ${COLORS.White};
`;

const FormContainer = styled.div`
  padding: 1rem;
  border-radius: 0.25rem;
  border: 1px solid ${COLORS.Orange600};
  flex: 1;

  ${tabletAndUpLinaria(`
    margin: 1rem auto 1.5rem auto;
    width: auto;
    max-width: 650px;
  `)}
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 1.5rem;
`;

const Headline = styled(Text)`
  line-height: 1.44;

  font-size: ${FONT_SIZES.px18};

  ${tabletAndUpLinaria(`
    font-size: ${FONT_SIZES.px24};
    line-height: 1.167;
  `)}
`;

const Subtitle = styled(Text)`
  line-height: 1.42;

  font-size: ${FONT_SIZES.px14};
`;
